import { useAuth } from "@/context/auth";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import useGetMerchantUsers from "@/services/merchantUser/hooks/useGetMerchantUsers";
import { useMemo } from "react";

// Who and what an invoice can be written for. A manager invoices for the whole team; a stylist (a chair
// renter too) only for themselves and the services assigned to them, and cannot change their own
// commission. Handled in the panel only for now.
export default function useInvoiceStaff() {
  const { isManager } = useDashboardAccess();
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const self = useGetMerchantForMerchantUser(phone).data;
  const team = useGetMerchantUsers();
  const services = useGetMerchantServices();

  const teamMembers = useMemo(
    () => (isManager ? team.data : team.data?.filter((member) => member.id === self?.id)),
    [isManager, team.data, self?.id],
  );
  const allServices = useMemo(() => {
    if (isManager) return services.data;
    const own = new Set((teamMembers?.[0]?.merchantServices ?? []).map((service) => service.id));
    return services.data
      ?.map((category) => ({ ...category, services: category.services?.filter((service) => own.has(service.id)) }))
      .filter((category) => (category.services?.length ?? 0) > 0);
  }, [isManager, services.data, teamMembers]);

  return { teamMembers, allServices, allServicesFetching: services.isFetching, commissionLocked: !isManager };
}

// The commission an invoice line starts with: the member's own, except a chair renter, who keeps the
// whole price whatever their profile says.
export const commissionOf = (member?: { markupPercentage?: number | null } | null) =>
  (member as { isChairRenter?: boolean } | null | undefined)?.isChairRenter ? 0 : member?.markupPercentage ?? 0;
